// "Müşteri Havuzuna Ekle" modalı — İl/İlçe/Mahalle/Grup kademeli filtreleri,
// serbest arama, Liste/Harita görünümü, çoklu seçim ve "Eklenecek ata" (frekans/gün).
// Backend: GET /api/customers/geo-options + /pool-search (yalnız OKUMA).
// #189 Seçilen müşteriler artık doğrudan POST edilmez; onStage ile parent'taki TASLAK
// duruma eklenir ve yalnız üstteki "Değişiklikleri Kaydet"te backend'e yazılır.
import { useState, useEffect, useCallback, useMemo } from 'react';
import { s, authHeaders } from '../listPageStyles';
import { pick, unwrap, FREQUENCY_OPTIONS, DAY_OPTIONS } from './routePlanUtils';
import RouteMap from './RouteMap';

// Talep 825 — Coğrafi seçenekler müşteri verisinden DISTINCT gelir; müşteri adları elle/import
// ile girildiği için aynı yer iki yazımla durabilir ("İzmir" ve "İZMİR"). SQL tarafındaki
// DISTINCT bunları ayırmaz çünkü kolon SEÇİMİ büyük/küçük harfe duyarlı okur, oysa DB collation
// Turkish_CI_AS'tir → sonraki `City == 'İzmir'` filtresi ikisini de yakalar. Yani ikisi AYNI
// filtreyi kuran mükerrer seçeneklerdir; listede iki kez görünmeleri sadece kafa karıştırır.
//
// Burada tekilleştirmek (veriyi toplu güncellemek yerine) bilinçli: yazımı düzeltmek bir VERİ
// kararıdır ve müşteri kayıtlarına dokunmayı gerektirir; bu ekranın işi değildir.
function dedupeGeo(values) {
  const seen = new Map();
  for (const v of values) {
    const key = String(v || '').trim().toLocaleUpperCase('tr-TR');
    if (!key) continue;
    // İlk görülen yazım korunur (backend OrderBy ile geldiği için istikrarlıdır).
    if (!seen.has(key)) seen.set(key, v);
  }
  return [...seen.values()];
}

async function fetchGeo(level, params = {}) {
  const qs = new URLSearchParams({ level, ...params }).toString();
  const r = await fetch(`/api/customers/geo-options?${qs}`, { headers: authHeaders() });
  if (!r.ok) return [];
  return dedupeGeo(unwrap(await r.json()) || []);
}

// Talep 903: İl filtresi kalktığı için tek seferde çekilecek üst sınır (sunucu tavanı 500).
const PAGE_LIMIT = 500;

export default function AddCustomersModal({ planId, vendorIds, defaultCity = '', stagedCustomerIds, onClose, onStage, showToast }) {
  // Talep 903: İl seçimi ARTIK ZORUNLU DEĞİL — ekran açılır açılmaz distribütör kapsamındaki
  // aktif müşteriler listelenir. defaultCity yalnızca kolaylık için ön-seçilir (Talep 810:
  // kilitli de değildir); kullanıcı İl'i temizleyip tüm kapsamı görebilir veya başka il
  // seçip oradan müşteri ekleyebilir (additive — zaten eklenenler silinmez).
  const [city, setCity] = useState(defaultCity || '');
  const [province, setProvince] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [groupId, setGroupId] = useState('');
  const [q, setQ] = useState('');

  const [cities, setCities] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [groups, setGroups] = useState([]);

  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('list');

  const [defFreq, setDefFreq] = useState('WEEKLY');
  // Çoklu gün seçimi (GEREKSİNİM 114): tek frekans + birden çok gün.
  const [defDays, setDefDays] = useState(() => new Set());

  // İl listesi + müşteri grupları (bir kez).
  useEffect(() => {
    fetchGeo('city').then(setCities);
    fetch('/api/customergroups/Select', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setGroups(Array.isArray(data) ? data : (data?.value || [])))
      .catch(() => setGroups([]));
  }, []);

  // İl değişince İlçe; İlçe değişince Mahalle.
  useEffect(() => {
    setProvince(''); setNeighborhood(''); setProvinces([]); setNeighborhoods([]);
    if (city) fetchGeo('province', { city }).then(setProvinces);
  }, [city]);
  useEffect(() => {
    setNeighborhood(''); setNeighborhoods([]);
    if (province) fetchGeo('neighborhood', { city, province }).then(setNeighborhoods);
  }, [province, city]);

  /**
   * #821 Arama bağımlılığı DİZİ REFERANSINA değil DEĞERE bakmalı.
   *
   * `vendorIds` prop'u çağıran tarafta her render'da yeniden üretilebilir; referansa bağlanan
   * arama, kapsam hiç değişmediği hâlde yeniden çalışıp `setSelected(new Set())` ile kullanıcının
   * seçimini siliyordu (haritadan alan seçince "Seçilenleri Ekle" pasif kalıyordu). Çağıran taraf
   * artık diziyi useMemo ile stabilize ediyor; bu satır ikinci savunma hattıdır — başka bir
   * çağıran aynı hataya düşerse ekran yine bozulmasın.
   */
  const vendorKey = (vendorIds || []).filter(Boolean).join(',');

  const search = useCallback(async () => {
    // Talep 903: İl ARTIK ZORUNLU DEĞİL. Ekran açılır açılmaz, hiçbir kriter verilmeden,
    // ilk sekmede seçili distribütörün AKTİF müşterileri alfabetik listelenir. Distribütör
    // seçili değilse liste kullanıcının yetkili olduğu distribütörlerle sınırlıdır — bu
    // daraltmayı backend `CustomerScopeFilter` (UserDataScope) yapar, istemci göndermez.
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (city) params.set('city', city);
      if (province) params.set('province', province);
      if (neighborhood) params.set('neighborhood', neighborhood);
      if (groupId) params.set('groupId', groupId);
      if (q.trim()) params.set('q', q.trim());
      if (planId) params.set('routePlanId', planId);
      // #737 Rut başlığında distribütör seçiliyse havuzu o distribütörün müşterileriyle daralt.
      // Seçili değilse parametre gönderilmez → kullanıcının yetkili olduğu tüm distribütörler.
      // #800 Planın kapsamı çoklu olabilir; her distribütör ayrı bir vendorId parametresi olarak
      // gider. Kapsam BOŞSA hiç parametre eklenmez → müşteri havuzu daraltılmaz (tüm distribütörler).
      // Referans değil DEĞER üzerinden (bkz. vendorKey açıklaması).
      vendorKey.split(',').forEach((vid) => { if (vid) params.append('vendorId', vid); });
      // Talep 903: yalnız aktif müşteriler (pasif/beklemede elenir).
      params.set('onlyActive', 'true');
      // İl filtresi kalktığı için sonuç kümesi büyüyebilir; sunucunun izin verdiği üst sınır.
      params.set('take', String(PAGE_LIMIT));
      const r = await fetch(`/api/customers/pool-search?${params.toString()}`, { headers: authHeaders() });
      if (!r.ok) throw new Error(`Arama başarısız (${r.status})`);
      setRows(unwrap(await r.json()) || []);
      setSelected(new Set());
    } catch (e) {
      showToast?.('error', e.message);
    } finally { setLoading(false); }
  }, [city, province, neighborhood, groupId, q, planId, vendorKey, showToast]);

  useEffect(() => { search(); }, [search]);

  // Talep 810: İl artık kilitlenmez; "Filtreyi temizle" tüm kriterleri (İl dahil) sıfırlar.
  const clearFilters = () => { setCity(''); setProvince(''); setNeighborhood(''); setGroupId(''); setQ(''); };

  const toggleRow = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // "Zaten planda" tespiti TASLAK duruma göredir (#189): parent'ın aktif müşteri id kümesi.
  // Böylece kaydedilmemiş eklemeler tekrar seçilemez, kaydedilmemiş çıkarmalar yeniden eklenebilir.
  const isAlready = useCallback(
    (id) => !!stagedCustomerIds && stagedCustomerIds.has(String(id)), [stagedCustomerIds]);
  const selectableRows = useMemo(() => rows.filter((r) => !isAlready(pick(r, 'id', 'Id'))), [rows, isAlready]);
  const allSelected = selectableRows.length > 0 && selectableRows.every((r) => selected.has(pick(r, 'id', 'Id')));
  const toggleAll = () => setSelected(() => (allSelected ? new Set() : new Set(selectableRows.map((r) => pick(r, 'id', 'Id')))));

  // #712 Harita noktaları YALNIZ rows'a bağlıdır (renk seçime göre değişse de points identity
  // sabit kalsın → alan seçiminde harita yeniden fit olup zoom sıçramasın). Seçim rengi RouteMap'e
  // selectedIds ile verilir. `id` = müşteri id'si (pool-search satırının id'si) — seçimle eşleşir.
  const mapPoints = useMemo(() => rows
    .map((r) => ({
      id: pick(r, 'id', 'Id'),
      lat: Number(pick(r, 'latitude', 'Latitude')),
      lng: Number(pick(r, 'longitude', 'Longitude')),
      label: pick(r, 'title', 'Title'),
      code: pick(r, 'textId', 'TextId'),
    }))
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng) && !(Math.abs(p.lat) < 0.001 && Math.abs(p.lng) < 0.001)), [rows]);

  // #712 Haritada çizilen alanın içindeki müşterileri seçime ekle (zaten planda olanlar atlanır).
  const selectByArea = useCallback((ids) => {
    const addable = ids.filter((id) => !isAlready(id));
    if (addable.length) {
      setSelected((prev) => {
        const next = new Set(prev);
        addable.forEach((id) => next.add(id));
        return next;
      });
    }
    showToast?.(addable.length ? 'success' : 'error',
      addable.length ? `${addable.length} müşteri alana göre seçildi` : 'Seçilen alanda eklenebilir müşteri yok');
  }, [isAlready, showToast]);

  const toggleDefDay = (val) => setDefDays((prev) => {
    const next = new Set(prev);
    if (next.has(val)) next.delete(val); else next.add(val);
    return next;
  });

  // #189 Seçimi backend'e YAZMAZ; parent'ın taslak durumuna ekler (onStage). Asıl yazım
  // üstteki "Değişiklikleri Kaydet"te tek seferde yapılır (bkz. useRoutePlanCustomers.flush).
  const add = () => {
    if (selected.size === 0) return;
    const days = [...defDays].sort((a, b) => a - b);
    const chosen = rows.filter((r) => selected.has(pick(r, 'id', 'Id')));
    onStage?.(chosen, defFreq || null, days, city);
    onClose?.();
  };

  const selOpt = (val, set, opts, placeholder) => (
    <select style={{ ...s.input, width: '100%' }} value={val} onChange={(e) => set(e.target.value)}>
      <option value="">{placeholder}</option>
      {opts}
    </select>
  );

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={{ ...s.modal, maxWidth: 980, width: '94%' }} onClick={(e) => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <div>
            <h3 style={s.modalTitle}>Müşteri Havuzuna Ekle</h3>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-secondary,#6b7280)' }}>
              Bölgeye göre filtreleyip çoklu seçin ve tek tek/topluca frekansa atayarak ekleyin.
            </p>
          </div>
          <button style={s.closeBtn} onClick={onClose}>×</button>
        </div>

        <div style={s.modalBody}>
          {/* Bilgi şeridi */}
          <div style={{
            display: 'flex', gap: 8, alignItems: 'center', padding: '10px 12px', borderRadius: 12,
            background: 'rgba(253,171,61,0.10)', border: '1px solid rgba(253,171,61,0.28)',
            color: '#92600a', fontSize: 12.5,
          }}>
            <span aria-hidden="true">⚡</span>
            {rows.length >= PAGE_LIMIT
              ? `İlk ${PAGE_LIMIT} aktif müşteri gösteriliyor — daraltmak için İl / İlçe / Mahalle / Grup filtrelerini veya aramayı kullanın.`
              : 'Aktif müşteriler listelenir. İl / İlçe / Mahalle / Grup ile filtreleyip toplu seçin — tek tek seçmenize gerek yok.'}
          </div>

          {/* Kademeli filtreler */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
            <div>
              <label style={s.label}>İl</label>
              {/* Talep 810/903: İl her zaman düzenlenebilir ve ZORUNLU DEĞİL — boş bırakılırsa
                  distribütör kapsamındaki tüm iller listelenir. */}
              {selOpt(city, setCity, cities.map((c) => <option key={c} value={c}>{c}</option>), 'Tümü')}
            </div>
            <div>
              <label style={s.label}>İlçe</label>
              {selOpt(province, setProvince, provinces.map((p) => <option key={p} value={p}>{p}</option>), city ? 'Tümü' : 'Önce il')}
            </div>
            <div>
              <label style={s.label}>Mahalle</label>
              {selOpt(neighborhood, setNeighborhood, neighborhoods.map((n) => <option key={n} value={n}>{n}</option>), province ? 'Tümü' : 'Önce ilçe')}
            </div>
            <div>
              <label style={s.label}>Müşteri Grubu</label>
              {selOpt(groupId, setGroupId, groups.map((g) => {
                const gid = g.id || g.Id; const gl = g.description || g.Description || g.name || g.Name || gid;
                return <option key={gid} value={gid}>{gl}</option>;
              }), 'Tümü')}
            </div>
          </div>

          {/* Arama + görünüm + sonuç sayısı */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              style={{ ...s.input, flex: 1, minWidth: 220 }}
              placeholder="İsim, kod veya adres ara…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <span style={{ fontSize: 12, color: 'var(--text-secondary,#6b7280)', whiteSpace: 'nowrap' }}>{rows.length} sonuç</span>
            <div style={{ display: 'inline-flex', borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border-color,#e5e7eb)' }}>
              <button onClick={() => setView('list')} style={viewBtn(view === 'list')}>☰ Liste</button>
              <button onClick={() => setView('map')} style={viewBtn(view === 'map')}>🗺 Harita</button>
            </div>
            <button style={s.btnSecondary} onClick={clearFilters}>Filtreyi temizle</button>
          </div>

          {/* İçerik — Talep 903: İl seçilmeden de liste gelir, "önce İl seçin" kapısı kaldırıldı. */}
          {view === 'map' ? (
            <RouteMap points={mapPoints} height={320} selectable onAreaSelect={selectByArea} selectedIds={selected} />
          ) : (
            <div style={{ border: '1px solid var(--border-color,#e5e7eb)', borderRadius: 12, overflow: 'auto', maxHeight: '46vh' }}>
              <table style={{ width: '100%', minWidth: 560, borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={cellHead(40)}><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>
                    <th style={cellHead()}>KOD</th>
                    <th style={cellHead()}>MÜŞTERİ</th>
                    <th style={cellHead()}>BÖLGE</th>
                    <th style={cellHead()}>GRUP</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={5} style={{ ...cell(), textAlign: 'center', color: '#9ca3af' }}>Yükleniyor…</td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan={5} style={{ ...cell(), textAlign: 'center', color: '#9ca3af' }}>Sonuç yok</td></tr>
                  ) : rows.map((r) => {
                    const id = pick(r, 'id', 'Id');
                    const already = isAlready(id);
                    return (
                      <tr key={id} style={{ background: selected.has(id) ? 'rgba(122,60,255,0.06)' : already ? 'rgba(148,163,184,0.08)' : 'transparent' }}>
                        <td style={cell()}>
                          <input type="checkbox" disabled={already} checked={selected.has(id)} onChange={() => toggleRow(id)} />
                        </td>
                        <td style={{ ...cell(), color: '#64748b' }}>{pick(r, 'textId', 'TextId') || '—'}</td>
                        <td style={cell()}>
                          <div style={{ fontWeight: 600 }}>{pick(r, 'title', 'Title') || '—'}</div>
                          <div style={{ fontSize: 11.5, color: '#94a3b8' }}>{pick(r, 'address', 'Address') || ''}</div>
                          {already ? <span style={pillStyle('#64748b')}>Zaten ekli</span> : null}
                        </td>
                        <td style={{ ...cell(), color: '#475569' }}>
                          {[pick(r, 'province', 'Province'), pick(r, 'neighborhood', 'Neighborhood')].filter(Boolean).join(' · ') || pick(r, 'region', 'Region') || '—'}
                        </td>
                        <td style={cell()}>
                          {pick(r, 'groupName', 'GroupName') ? <span style={pillStyle('#0073ea')}>{pick(r, 'groupName', 'GroupName')}</span> : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div style={{ ...s.modalFooter, flexDirection: 'column', alignItems: 'stretch', gap: 10 }}>
          {/* Tek frekans + ÇOKLU gün seçici (GEREKSİNİM 114) */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary,#6b7280)' }}>Eklenecek ata:</span>
            <select style={{ ...s.input, padding: '6px 10px' }} value={defFreq} onChange={(e) => setDefFreq(e.target.value)}>
              {FREQUENCY_OPTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
            <span style={{ fontSize: 12, color: 'var(--text-secondary,#9ca3af)' }}>Gün(ler):</span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {DAY_OPTIONS.map((d) => {
                const on = defDays.has(d.value);
                return (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => toggleDefDay(d.value)}
                    title={d.label}
                    style={dayChip(on, d.color)}
                  >
                    {d.short}
                  </button>
                );
              })}
            </div>
            <span style={{ fontSize: 11.5, color: 'var(--text-secondary,#9ca3af)' }}>
              {defDays.size === 0 ? 'gün seçilmezse sadece havuza eklenir' : `${defDays.size} gün`}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 12, color: 'var(--text-secondary,#9ca3af)' }}>
              {selected.size} müşteri seçili · ekleme “Değişiklikleri Kaydet”e kadar kesinleşmez
            </span>
            <div style={{ display: 'flex', gap: 10 }}>
              <button style={s.btnSecondary} onClick={onClose}>Vazgeç</button>
              <button
                style={{ ...s.btnPrimary, opacity: selected.size === 0 ? 0.55 : 1 }}
                onClick={add}
                disabled={selected.size === 0}
              >
                {`Seçilenleri Ekle (${selected.size})`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const cellHead = (w) => ({
  padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
  color: 'var(--text-secondary,#6b7280)', borderBottom: '1px solid var(--border-color,#e5e7eb)',
  background: 'var(--bg-secondary,#f8fafc)', width: w, position: 'sticky', top: 0,
});
const cell = () => ({ padding: '9px 12px', borderBottom: '1px solid var(--border-color,#f1f5f9)', verticalAlign: 'top' });
const viewBtn = (active) => ({
  border: 'none', padding: '7px 12px', fontSize: 13, cursor: 'pointer', fontWeight: active ? 700 : 500,
  background: active ? 'var(--accent)' : 'var(--input-bg,#fff)', color: active ? '#fff' : 'var(--text-primary,#1f2937)',
});
const pillStyle = (color) => ({
  display: 'inline-block', marginTop: 4, padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700,
  color, background: `${color}1a`, border: `1px solid ${color}33`,
});
const dayChip = (on, color) => ({
  minWidth: 38, padding: '6px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: 'pointer',
  color: on ? '#fff' : color,
  background: on ? color : `${color}14`,
  border: `1px solid ${on ? color : `${color}55`}`,
  transition: 'all .12s ease',
});
